import { useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { UserSettings } from '@atnt/shared';
import { meQuery } from '@/shared/api/auth';
import { applyUserPreferences } from '@/shared/lib/preferences';

/**
 * "Interfeys sozlamalari" applied to the document (556:16647, 01-design-system §1.6 / §8.3):
 *   locale        → i18n language + <html lang>             (shared applyUserPreferences)
 *   accentColor   → --ds-accent                              (shared applyUserPreferences)
 *   theme         → <html data-theme="light|dark">; "system" follows prefers-color-scheme live
 *   fontSize      → --ui-user-scale = fontSize / 14          (scales the whole rem-based UI: 12…16 → 0.857…1.143)
 *   animations    → <html data-motion="off"> when false      (tokens.css zeroes transitions/animations)
 *   compactTables → <html data-density="compact">            (tables read it through useCompactTables)
 * Used for the live preview of the settings page and after saving; call it at boot with the saved settings.
 */

export const FONT_SIZES = [12, 13, 14, 15, 16] as const;
export type FontSize = (typeof FONT_SIZES)[number];

/** Interface defaults (userSettingsSchema): dark theme, accent #0275FF, 14 px text, animations on. */
export const DEFAULT_INTERFACE: Pick<UserSettings, 'locale' | 'theme' | 'accentColor' | 'fontSize' | 'compactTables' | 'animations'> = {
  locale: 'uz-Latn',
  theme: 'dark',
  accentColor: '#0275FF',
  fontSize: 14,
  compactTables: false,
  animations: true,
};

const systemQuery = () => (typeof window === 'undefined' ? null : window.matchMedia('(prefers-color-scheme: light)'));
let systemListener: (() => void) | null = null;

export const resolveTheme = (theme: UserSettings['theme'] | undefined): 'light' | 'dark' =>
  theme === 'system' ? (systemQuery()?.matches ? 'light' : 'dark') : theme === 'light' ? 'light' : 'dark';

export function applyInterfacePreferences(settings: Partial<UserSettings>) {
  const root = document.documentElement;
  const theme = settings.theme;
  applyUserPreferences({ ...settings, theme: theme ? resolveTheme(theme) : undefined });

  // "Tizim": keep following the OS while selected
  const mq = systemQuery();
  if (mq && systemListener) {
    mq.removeEventListener('change', systemListener);
    systemListener = null;
  }
  if (mq && theme === 'system') {
    systemListener = () => {
      root.dataset.theme = mq.matches ? 'light' : 'dark';
    };
    mq.addEventListener('change', systemListener);
  }

  if (settings.fontSize != null) {
    const scale = settings.fontSize / 14;
    if (Math.abs(scale - 1) < 1e-6) root.style.removeProperty('--ui-user-scale');
    else root.style.setProperty('--ui-user-scale', scale.toFixed(4));
    // canvases (ECharts, stage) re-measure on resize
    window.dispatchEvent(new Event('resize'));
  }
  if (settings.animations != null) {
    if (settings.animations) delete root.dataset.motion;
    else root.dataset.motion = 'off';
  }
  // always explicit once applied, so the live preview of "off" wins over a saved "on"
  if (settings.compactTables != null) root.dataset.density = settings.compactTables ? 'compact' : 'default';
}

/* ───────────────────────── compact tables ───────────────────────── */

const densityListeners = new Set<() => void>();
let densityObserver: MutationObserver | null = null;

function subscribeDensity(cb: () => void) {
  densityListeners.add(cb);
  if (!densityObserver && typeof MutationObserver !== 'undefined') {
    densityObserver = new MutationObserver(() => densityListeners.forEach((l) => l()));
    densityObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-density'] });
  }
  return () => {
    densityListeners.delete(cb);
    if (!densityListeners.size) {
      densityObserver?.disconnect();
      densityObserver = null;
    }
  };
}

const readDensity = () => (typeof document === 'undefined' ? undefined : document.documentElement.dataset.density);

/**
 * "Ixcham jadval": true when tables should use the compact density (rows 28, padding 4/12). Follows the live preview
 * of the settings page (`<html data-density>`) and falls back to the saved setting.
 *
 *   <DataTable density={useCompactTables() ? 'compact' : 'default'} … />
 */
export function useCompactTables(): boolean {
  const attr = useSyncExternalStore(subscribeDensity, readDensity, () => undefined);
  const { data } = useQuery(meQuery);
  if (attr !== undefined) return attr === 'compact';
  return data?.user.settings.compactTables ?? false;
}

/** DataTable `density` prop for the current setting. */
export function useTableDensity(): 'compact' | 'default' {
  return useCompactTables() ? 'compact' : 'default';
}
